<template>
  <div class="business-settings-inline-form">
    <v-form ref="certificateForm" @submit.prevent="submit">
      <v-card class="cert-dialog-card" flat>
        <v-card-text>
          <!-- Company Name -->
          <div class="mb-5">
            <div class="company-label">Firma</div>
            <div class="company-name">{{ company.name }}</div>
          </div>

          <!-- File Input -->
          <v-file-input
            autofocus
            label="Učitaj certifikat *"
            accept=".p12, .pfx, .pem, .crt, .cer, .ca-bundle, .p7b, .p7c, .p7s"
            :rules="[rules.req, rules.certSize]"
            @change="certFileChange"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-file-certificate"
            class="form-input mb-4"
          >
          </v-file-input>

          <!-- Password Input -->
          <v-text-field
            label="Lozinka certifikata *"
            v-model="certPass"
            ref="certPass"
            outlined
            dense
            :rules="[rules.req]"
            :type="passVisible ? 'text' : 'password'"
            :append-icon="passVisible ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="() => (passVisible = !passVisible)"
            class="form-input"
          ></v-text-field>
        </v-card-text>

        <v-card-actions class="d-flex justify-end form-footer-sticky">
          <v-btn
            class="btn-primary"
            :loading="submitting"
            :disabled="submitting"
            type="submit"
            elevation="0">
            <v-icon left small color="#fff">mdi-content-save</v-icon>
            Spremi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>
<script>
import rules from '@/plugins/rules'

import { df, auth } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'CertificateUploadInline',
  inject: ['showMsgBox', 'showLoader', 'hideLoader', 'showAlert'],
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    certificate: undefined,
    certPass: undefined,
    business: undefined,
    passVisible: false,
    submitting: false,
    unsubscribe: undefined,
    unsubscribeError: undefined,
    listeners: [],
    rules: {
      req: rules.req(),
      certSize: (value) =>
        !value ||
        value.size < 1048487 ||
        'Najveća dozvoljena veličina dokumenta je 1MB.'
    }
  }),
  methods: {
    resetForm () {
      this.$refs.certificateForm.reset()
      this.submitting = false
      this.unsubscribeListeners()
    },
    toBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    async certFileChange (_file) {
      if (_file) {
        const file = await this.toBase64(_file)
        try {
          this.certificate = file.split(',')[1]
        } catch (err) {
          this.certificate = undefined
          this.showMsgBox({
            text: err,
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'error'
          })
        }
      }
    },
    unsubscribeListeners () {
      this.listeners.forEach((listener) => {
        listener()
        this.listeners = this.listeners.filter((l) => l !== listener)
      })
    },
    submit () {
      if (!this.$refs.certificateForm.validate()) return
      const reqId = uuidv4()
      const payload = {
        action: {
          operation: 'set',
          entity: 'certificate',
          params: {
            cert: this.certificate,
            // hash: `web.${this.certPass}.${auth.currentUser.uid}`,
            company_id: this.company.id,
            password: this.certPass
          }
        }
      }

      this.submitting = true
      df.doc(`request/${reqId}`)
        .set({
          msg_id: reqId,
          user_id: `${auth.currentUser.uid}`,
          device_id: 'web',
          created: `${new Date().getTime()}`,
          type: 'certificate',
          payload: btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
        })
        // .then(function (doc) {
        // })
        // .catch(function (error) {
        // })
        .finally(() => {})

      const docRef = df.doc(`response/${reqId}`)
      this.listeners.push(
        docRef.onSnapshot((doc) => {
          if (doc && doc.data() && doc.data().header && doc.data().header.code === 200) {
            this.submitting = false
            this.showMsgBox({
              text: 'Certifikat je uploadan!',
              actions: ['cancel'],
              cancelBtnText: 'OK'
            })
            this.$emit('saved')
            this.resetForm()
          }
        })
      )

      const errorRef = df.collection('errors')
      this.listeners.push(
        errorRef
          .where('error_id', '==', `${auth.currentUser.uid}.${reqId}`)
          .onSnapshot((documentSnapshots) => {
            if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
              documentSnapshots.docs.forEach((item) => {
                const error = item.data()
                if (error && error.error_status) {
                  let erStatus = ''
                  if (error.error_status.includes('Invalid certificate password:')) {
                    erStatus = 'Neispravna lozinka certifikata'
                    this.$refs.certPass.reset()
                  }
                  this.showMsgBox({
                    text: erStatus || error.error_status,
                    actions: ['cancel'],
                    cancelBtnText: 'OK',
                    color: 'error'
                  })
                  this.submitting = false
                }
              })
            }
          })
      )
    }
  },
  beforeDestroy () {
    this.unsubscribeListeners()
  }
}
</script>
<style scoped>
.company-label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #64748B !important;
  text-transform: uppercase;
  letter-spacing: 0.5px !important;
  margin-bottom: 8px;
}

.company-name {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  padding: 12px 16px;
  background: #F8FAFC !important;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}

.form-input >>> .v-input__slot {
  border-radius: 12px !important;
  background: #ffffff !important;
  min-height: 40px !important;
}

.form-input >>> fieldset {
  border-color: #e5e7eb !important;
  border-width: 1px !important;
  border-radius: 12px !important;
}

.form-input >>> .v-input__slot:hover fieldset {
  border-color: #00BCD4 !important;
}

.form-input.v-input--is-focused >>> fieldset {
  border-color: #00BCD4 !important;
  border-width: 2px !important;
}

.form-input >>> input {
  font-size: 13px !important;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 22px;
  background-color: #00BCD4 !important;
  color: #fff !important;
  border: none !important;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,188,212,0.3);
  transition: background-color 0.15s;
  text-transform: none !important;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0097A7 !important;
}

.btn-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-secondary {
  padding: 11px 20px;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #64748B;
  cursor: pointer;
  transition: all 0.15s;
  text-transform: none !important;
  box-shadow: none !important;
}

.btn-secondary:hover {
  background: #F1F5F9;
  border-color: #CBD5E1;
  color: #334155;
}

.business-settings-inline-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.business-settings-inline-form>>>form {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.business-settings-inline-form>>>.v-card {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.business-settings-inline-form>>>.v-card__text {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.form-footer-sticky {
  flex: 0 0 auto;
  background: #fff;
  border-top: 1px solid #F3F4F6;
  padding: 16px 24px !important;
}
</style>
